import type { Scene } from "@babylonjs/core/scene";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { getAssetManager, type InstantiatedModel } from "../../systems/AssetManager";
import { tagAs } from "../PlaceholderTag";
import placement from "../../../assets/models-src/environment/canopy_leo_parlor.json";
import { scopeCanopyReadingLight } from "./CanopyReadingLight";

/** One optional furniture source, opaque supported fallback and physical lamp.
 * The layout owns the deck; the Elder and memory retain their own state. */
export class CanopyParlorPresentation {
  readonly ready: Promise<boolean>;
  readonly readingLight: PointLight;
  readonly seatY: number;
  readonly leoPosition: Vector3;
  readonly anchorPosition: Vector3;
  readonly yaw=placement.leo.yaw;
  private readonly root: TransformNode;
  private readonly readingBounce: PointLight;
  private readonly fallback: AbstractMesh[]=[];
  private readonly materials:PBRMaterial[]=[];
  private casters:AbstractMesh[]=[];
  private targets:AbstractMesh[]=[];
  private source:InstantiatedModel|null=null;
  private retired=false;
  private releaseLighting:(()=>void)|null=null;
  private readonly observer;

  constructor(private readonly scene:Scene,private readonly shadows:ShadowGenerator,floorY:number,visitors:readonly AbstractMesh[]=[]){
    this.root=new TransformNode("canopyParlor",scene);this.root.position.set(placement.sceneX,floorY,0);
    this.seatY=floorY+placement.seatTopY;
    this.leoPosition=new Vector3(placement.sceneX+placement.leo.x,this.seatY+.8,placement.leo.z);
    this.anchorPosition=new Vector3(placement.sceneX+placement.anchor.x,floorY+placement.anchor.y,placement.anchor.z);
    const leather=this.material("leather",new Color3(.13,.065,.035)),iron=this.material("iron",new Color3(.055,.065,.06)),wool=this.material("wool",new Color3(.055,.14,.095));
    const box=(name:string,p:readonly number[],size:readonly number[],mat:PBRMaterial)=>{
      const m=MeshBuilder.CreateBox("canopyParlorFallback_"+name,{width:size[0],height:size[1],depth:size[2]},scene);
      m.parent=this.root;m.position.set(p[0],p[1],p[2]);m.material=mat;m.isPickable=false;m.receiveShadows=true;tagAs(m,"placeholder");this.fallback.push(m);
    };
    box("seat",[0,.61,-1.75],[2.05,.18,2.34],leather);
    box("back",[0,1.58,-2.86],[2.1,2.14,.22],leather);
    for(const x of[-.72,.72]){
      box("foot",[x,.03,-.78],[.36,.06,.30],iron);
      const m=MeshBuilder.CreateTube("canopyParlorFallback_brace",{path:[new Vector3(x,.06,-.78),new Vector3(x,.43,-1.65),new Vector3(x,.53,-2.53)],radius:.07,tessellation:12},scene);
      m.parent=this.root;m.material=iron;m.isPickable=false;m.receiveShadows=true;this.fallback.push(m);
    }
    for(const x of[-1.05,1.05])box("arm",[x,1.08,-1.7],[.16,.16,2.05],leather);
    box("sweater",[-.98,1.17,-.85],[.58,.06,.5],wool);
    box("sweaterFold",[-.98,.84,-.57],[.58,.66,.025],wool);
    this.readingLight=new PointLight("canopyParlor_readingLamp",new Vector3(placement.sceneX+placement.lamp.x,floorY+placement.lamp.y,placement.lamp.z),scene);
    this.readingLight.diffuse.set(.85,.65,.36);this.readingLight.specular=Color3.Black();this.readingLight.intensity=3.5;this.readingLight.range=4.5;
    // Visiting skins keep their existing world/harness lighting and material
    // budgets. The physical range confines this borrowed lamp to the parlor.
    this.readingLight.includedOnlyMeshes=[...visitors];
    // A restrained warm bounce exposes the front-facing fur that the rear
    // practical cannot reach. It shares the parlor's lifetime and range;
    // the Elder and furniture retain the actual lamp rather than this fill.
    this.readingBounce=new PointLight("canopyParlor_readingBounce",new Vector3(placement.sceneX-1.2,floorY+1.25,1.4),scene);
    this.readingBounce.diffuse=this.readingLight.diffuse.clone();this.readingBounce.specular=Color3.Black();
    this.readingBounce.intensity=1.2;this.readingBounce.range=4.5;
    this.readingBounce.includedOnlyMeshes=[...visitors];this.readingBounce.setEnabled(visitors.length>0);
    this.select(this.fallback);this.observer=scene.onDisposeObservable.addOnce(()=>this.dispose());this.ready=this.load();
  }
  private material(name:string,color:Color3):PBRMaterial{
    const m=new PBRMaterial("canopyParlorFallback_"+name,this.scene);m.albedoColor=color;m.roughness=.9;this.materials.push(m);return m;
  }
  private select(meshes:readonly AbstractMesh[]):void{
    this.releaseLighting?.();this.releaseLighting=null;
    for(const mesh of this.casters)this.shadows.removeShadowCaster(mesh,false);
    this.casters=meshes.filter(m=>m.getTotalVertices()>0);
    for(const mesh of this.casters){
      this.shadows.addShadowCaster(mesh,false);
      const material=mesh.material as {maxSimultaneousLights?:number}|null;
      if(typeof material?.maxSimultaneousLights==="number")material.maxSimultaneousLights=4;
    }
    const readers=this.readingLight.includedOnlyMeshes.filter(m=>!this.targets.includes(m));
    this.targets=[...this.casters];this.readingLight.includedOnlyMeshes=[...readers,...this.targets];
    this.releaseLighting=scopeCanopyReadingLight(this.scene,this.readingLight,this.targets);
  }
  private async load():Promise<boolean>{
    let candidate:InstantiatedModel|null=null;
    try{
      candidate=await getAssetManager().instantiateModel(this.scene,"/assets/models/environment/canopy_leo_parlor.glb",undefined,{doNotInstantiate:true});
      if(this.retired||this.scene.isDisposed){candidate.dispose();return false;}
      const geometry=candidate.meshes.filter(m=>m.getTotalVertices()>0),clean=(m:AbstractMesh)=>m.name.replace(/^(Clone of )+/,"");
      for(const name of["seat","upholstery","sweater","supports"])if(geometry.filter(m=>clean(m)==="CanopyParlor_"+name).length!==1)throw Error("Missing/duplicate parlor "+name);
      if(!candidate.rootNodes.length||geometry.some(m=>!m.isEnabled()||!m.isVisible||m.visibility!==1||!m.material||m.material.alpha!==1||m.material.needAlphaBlendingForMesh(m)||m.material.needAlphaTestingForMesh(m)))throw Error("Incomplete opaque parlor source");
      for(const root of candidate.rootNodes)root.parent=this.root;
      const seat=geometry.find(m=>clean(m)==="CanopyParlor_seat")!;seat.computeWorldMatrix(true);
      if(Math.abs(seat.getBoundingInfo().boundingBox.maximumWorld.y-this.seatY)>1e-5)throw Error("Parlor seat/support mismatch");
      for(const mesh of geometry){mesh.isPickable=false;mesh.receiveShadows=true;tagAs(mesh,"modular-final");}
      this.source=candidate;this.select(geometry);for(const m of this.fallback)m.isVisible=false;return true;
    }catch(error){candidate?.dispose();if(!this.retired&&!this.scene.isDisposed)console.warn("[CanopyParlor] Keeping supported fallback:",error);return false;}
  }
  dispose():void{
    if(this.retired)return;this.retired=true;this.scene.onDisposeObservable.remove(this.observer);
    this.releaseLighting?.();this.releaseLighting=null;
    for(const mesh of this.casters)this.shadows.removeShadowCaster(mesh,false);this.casters=[];
    this.readingLight.dispose();this.readingBounce.dispose();this.targets=[];this.source?.dispose();this.source=null;this.root.dispose();for(const m of this.materials)m.dispose();
  }
}
